/**
 * Aseli cart gift — free-gift comfort layer on top of native Shopify discounts.
 *
 * Architecture (see docs/gutscheine.md):
 *  - The gift code itself is a server-side BxGY discount ("buy >= threshold, get
 *    1x gift product free"). This script NEVER decides about money — it only
 *    adds/removes the gift line and sets the checkout discount session.
 *  - Which code triggers which gift comes from ACTIVE free_gift_action metaobjects,
 *    serialized into <script data-aseli-gift-catalog> by aseli-gift-catalog.liquid.
 *  - One field for ALL codes: unknown codes just get a /discount/<CODE> session
 *    ("wird im Checkout geprüft") — validity stays Shopify's call.
 *  - Single-code gate (Aseli decision 2026-10-07): codes never stack. Entering
 *    a gift code while another code is active (or the other way round) is
 *    blocked with a status message; switching between two gift codes stays ok.
 *  - Gift-exclusive codes (Aseli decision 2026-10-20, first: eimer10): regular
 *    product-discount codes listed in the gift_choice_action metaobjects never
 *    combine with the threshold gift — entering one retires the choice line
 *    (removal owned by snippet-aseli-gift-choice.js) and the status explains it.
 *
 * Loading: site-wide via theme.liquid (after pubsub/constants) — the add/remove logic
 * must also run on non-cart pages (PDP add-to-cart can cross the threshold); the
 * visible widget is rendered by aseli-cart-gift.liquid on the cart page/drawer only.
 *
 * Dawn robustness: cart drawer / cart page swap their DOM on every cart change, so
 * this uses document-level event delegation + a MutationObserver for hydration
 * (an input draft would otherwise be lost when the drawer re-renders).
 */
(function () {
  'use strict';

  // The snippet can be rendered twice (drawer + cart page); the script tag itself
  // is only executed on first paint — innerHTML swaps never execute scripts.
  if (window.aseliCartGiftInit) return;
  window.aseliCartGiftInit = true;

  var SOURCE = 'aseli-cart-gift';
  var LS_KEY = 'aseli:cart-gift';
  var GIFT_PROP = '_gift';
  var CHOICE_PROP = '_gift_choice';
  // Fired by snippet-aseli-gift-choice.js after its threshold-gift line yielded to
  // our code gift (two spend-threshold BxGYs never both apply — non-Plus limit).
  var YIELDED_EVENT = 'aseli:gift-choice-yielded';
  // Fired by us while a gift-exclusive regular code is active and the choice line
  // still sits in the cart — snippet-aseli-gift-choice.js owns that line and
  // removes it (single owner per line, no racing cart mutations).
  var RETIRE_EVENT = 'aseli:gift-choice-retire';

  var SELECTORS = {
    catalog: '[data-aseli-gift-catalog]',
    root: '[data-aseli-gift-root]',
    form: '[data-aseli-gift-form]',
    input: '[data-aseli-gift-input]',
    status: '[data-aseli-gift-status]',
    chip: '[data-aseli-gift-chip]',
    chipCode: '[data-aseli-gift-chip-code]',
    chipClear: '[data-aseli-gift-clear]',
  };

  var state = {
    code: null, // active code (persisted)
    entry: null, // catalog entry for the code (null = regular code)
    declined: false, // user removed the gift on purpose — no auto re-add
    yielded: false, // gift-choice line gave way to this code gift — explain it
    busy: false, // mutation in flight
    selfRemoved: false, // we removed the line ourselves (not the user)
    cart: null, // last cart snapshot
    draft: '', // typed, not yet submitted (survives drawer re-renders)
    sessionSetAt: 0, // last applyCode — server mirror must not race the /discount/ fetch
    sessionClearedAt: 0, // last clearCode — adoption must not resurrect the removed code
    blocked: null, // { code, enteringGift } — gate rejected the last entry attempt
    exclusive: {}, // gift-exclusive codes (lowercased) — see readExclusiveCodes()
    choiceYielded: null, // exclusive code that removed the threshold gift — explain it
  };

  var fmtEUR = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });

  function routes() {
    return window.routes || { cart_url: '/cart', cart_add_url: '/cart/add', cart_change_url: '/cart/change' };
  }

  // ---------------------------------------------------------------- catalog / state

  function catalog() {
    var el = document.querySelector(SELECTORS.catalog);
    if (!el) return [];
    try {
      return JSON.parse(el.textContent) || [];
    } catch (err) {
      return [];
    }
  }

  function findEntry(code) {
    var list = catalog();
    for (var i = 0; i < list.length; i++) {
      if (list[i].code === code) return list[i];
    }
    return null;
  }

  // Gift-exclusive codes (first: eimer10) — rendered globally by
  // aseli-gift-choice.liquid from the gift_choice_action metaobjects (comma
  // list per action). They displace the threshold gift like a code gift does.
  function readExclusiveCodes() {
    var codes = {};
    var nodes = document.querySelectorAll('[data-aseli-exclusive-codes]');
    for (var i = 0; i < nodes.length; i++) {
      try {
        var parsed = JSON.parse(nodes[i].textContent);
        if (Array.isArray(parsed)) {
          for (var e = 0; e < parsed.length; e++) {
            if (parsed[e]) codes[String(parsed[e]).toLowerCase()] = true;
          }
        }
      } catch (err) {
        /* no list — exclusive handling simply stays off */
      }
    }
    return codes;
  }

  function isExclusive(code) {
    return !!(code && state.exclusive[String(code).toLowerCase()]);
  }

  function persist() {
    try {
      if (state.code) localStorage.setItem(LS_KEY, JSON.stringify({ code: state.code }));
      else localStorage.removeItem(LS_KEY);
    } catch (err) {
      /* private mode etc. — session-only then */
    }
  }

  // ---------------------------------------------------------------- cart helpers

  function isGiftLine(item) {
    return !!(item && item.properties && item.properties[GIFT_PROP]);
  }

  function giftLine(cart, code) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (isGiftLine(items[i]) && (!code || items[i].properties[GIFT_PROP] === code)) return items[i];
    }
    return null;
  }

  function isChoiceLine(item) {
    return !!(item && item.properties && item.properties[CHOICE_PROP]);
  }

  function choiceLine(cart) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (isChoiceLine(items[i])) return items[i];
    }
    return null;
  }

  // First gift-exclusive code in the server session (entered at the checkout or
  // via a /discount/ marketing link — applyCode never ran for those).
  function activeExclusiveIn(cart) {
    var codes = (cart && cart.discount_codes) || [];
    for (var i = 0; i < codes.length; i++) {
      var c = codes[i] && String(codes[i].code || '').toLowerCase();
      if (c && state.exclusive[c]) return c;
    }
    return null;
  }

  // Threshold base excludes the gift line itself — stricter than Shopify (which checks
  // the subtotal incl. gift position), so the server never rejects what we accepted.
  function baseTotal(cart) {
    var items = (cart && cart.items) || [];
    var total = 0;
    for (var i = 0; i < items.length; i++) {
      if (!isGiftLine(items[i])) total += items[i].final_line_price;
    }
    return total;
  }

  // ---------------------------------------------------------------- status UI

  // Status copy lives on the widget root as data-acg-tpl-* attributes (rendered by
  // aseli-cart-gift.liquid via locale strings) so it follows the storefront language.
  // German literals below are last-resort fallbacks only.
  function tpl(root, name, fallback) {
    var v = root ? root.getAttribute('data-acg-tpl-' + name) : null;
    return v != null && v !== '' ? v : fallback;
  }

  function fill(text, vars) {
    return String(text).replace(/\{(\w+)\}/g, function (m, key) {
      return vars[key] != null ? vars[key] : m;
    });
  }

  function statusText(root) {
    if (state.blocked) {
      // gate message wins over every other status — it explains the failed entry
      var key = state.blocked.enteringGift ? 'blocked-gift' : 'blocked-code';
      var fallback = state.blocked.enteringGift
        ? 'Der Geschenk-Code ist nicht mit dem Rabattcode {code} kombinierbar. Entferne ihn zuerst über das ×, dann gib den Geschenk-Code erneut ein.'
        : 'Dieser Code ist nicht mit dem Geschenk-Code {code} kombinierbar. Entferne ihn zuerst über das ×, dann gib deinen Code erneut ein.';
      return fill(tpl(root, key, fallback), { code: state.blocked.code || '' });
    }
    if (!state.code) return '';
    if (!state.entry) {
      if (state.choiceYielded === state.code) {
        // exclusive code took the threshold gift's place — explain the removal
        return fill(
          tpl(root, 'removed-choice', 'Der Code {code} ist nicht mit dem Geschenk kombinierbar. Dein Auswahl-Geschenk wurde aus dem Warenkorb entfernt.'),
          { code: state.code }
        );
      }
      return tpl(root, 'applied', 'Code übernommen. Er wird beim Checkout geprüft.');
    }
    var base = baseTotal(state.cart);
    var title = state.entry.title;
    var text;
    if (giftLine(state.cart, state.code)) {
      text = fill(tpl(root, 'in-cart', 'Geschafft — dein Geschenk ({title}) liegt im Warenkorb.'), { title: title });
    } else if (state.declined) {
      text = tpl(root, 'removed', 'Dein Geschenk wurde entfernt. Gib den Code erneut ein, um es wieder zu erhalten.');
    } else {
      var missing = state.entry.threshold - base;
      if (missing > 0) {
        text = fill(tpl(root, 'missing', 'Noch {amount} bis zu deinem Geschenk: {title}'), {
          amount: fmtEUR.format(missing / 100),
          title: title
        });
      } else {
        text = fill(tpl(root, 'waiting', 'Dein Geschenk ({title}) wartet auf dich.'), { title: title });
      }
    }
    if (state.yielded) {
      // the choice gift gave way to this code gift — append the explanation
      text += ' ' + tpl(root, 'yielded', 'Dein Auswahl-Geschenk wurde entfernt: Der Geschenk-Code ist nicht mit der Geschenk-Auswahl kombinierbar.');
    }
    return text;
  }

  function setText(el, text) {
    // Only touch the DOM when the value actually changes — writing the SAME string
    // still replaces the text node, which feeds the MutationObserver below and
    // used to create an endless hydrate loop (cart page froze, 2026-08-24).
    if (el && el.textContent !== text) el.textContent = text;
  }

  function syncWidget() {
    var roots = document.querySelectorAll(SELECTORS.root);
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      var input = root.querySelector(SELECTORS.input);
      // never fight the customer's cursor while they are typing
      if (input && document.activeElement !== input) input.value = state.draft || state.code || '';
      var status = root.querySelector(SELECTORS.status);
      setText(status, statusText(root));
      var chip = root.querySelector(SELECTORS.chip);
      if (chip) {
        var show = !!state.code;
        if (chip.hidden === show) chip.hidden = !show; // only write on change
        setText(chip.querySelector(SELECTORS.chipCode), state.code || '');
      }
    }
  }

  function hydrateRoots() {
    // Mark + fill widget nodes that Dawn's section swaps newly injected.
    var roots = document.querySelectorAll('[data-aseli-gift-root]:not([data-hydrated])');
    if (!roots.length) return;
    for (var i = 0; i < roots.length; i++) roots[i].setAttribute('data-hydrated', '1');
    syncWidget();
  }

  // ---------------------------------------------------------------- section rendering

  function getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  function sectionsToRequest() {
    // Mirror Dawn's own render targets so our add/remove keeps every cart region fresh.
    // Only sections whose host element is in the DOM — the script runs site-wide now
    // (e.g. a PDP has none of the cart sections; a null element would crash rendering).
    var list = [];
    if (document.getElementById('main-cart-items')) {
      list.push({ id: 'main-cart-items', section: document.getElementById('main-cart-items').dataset.id, selector: '.js-contents' });
    }
    if (document.getElementById('main-cart-footer')) {
      list.push({ id: 'main-cart-footer', section: document.getElementById('main-cart-footer').dataset.id, selector: '.js-contents' });
    }
    if (document.querySelector('cart-drawer')) {
      list.push({ id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' });
    }
    if (document.getElementById('cart-icon-bubble')) {
      list.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
    }
    return list;
  }

  function renderSections(parsedState) {
    sectionsToRequest().forEach(function (section) {
      var html = parsedState.sections && parsedState.sections[section.section];
      var byId = document.getElementById(section.id);
      if (!html || !byId) return;
      var elementToReplace = byId.querySelector(section.selector) || byId;
      elementToReplace.innerHTML = getSectionInnerHTML(html, section.selector);
    });
  }

  // Re-render the cart regions WITHOUT a cart mutation. Code entries that lead to
  // no add/remove (exclusive code without a choice line, gift code below its
  // threshold) never trigger Dawn's section rendering — but the cart strip
  // (Kachel) renders server-side from cart.discount_codes and would keep showing
  // its pre-code state until the next cart event (browser report 2026-10-20).
  function refreshSections() {
    var sections = sectionsToRequest();
    if (!sections.length) return;
    var ids = sections
      .map(function (s) {
        return s.section;
      })
      .join(',');
    fetch(routes().cart_url + '?sections=' + encodeURIComponent(ids), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (map) {
        sections.forEach(function (section) {
          var html = map && map[section.section];
          var byId = document.getElementById(section.id);
          if (!html || !byId) return;
          var elementToReplace = byId.querySelector(section.selector) || byId;
          elementToReplace.innerHTML = getSectionInnerHTML(html, section.selector);
        });
      })
      .catch(function () {});
  }

  // ---------------------------------------------------------------- gift mutations

  function setSessionDiscount(code) {
    // /discount/<CODE> sets the checkout discount session cookie — the customer never
    // re-enters the code at checkout. Always "succeeds"; Shopify validates at checkout.
    // The promise (redirect chain included) is returned so applyCode can wait for the
    // session before rendering the gift line — otherwise the cart section would arrive
    // without the BxGY allocation and show a price where "Geschenkt" belongs.
    return fetch('/discount/' + encodeURIComponent(code), { credentials: 'same-origin', keepalive: true }).catch(function () {});
  }

  function clearSessionDiscount() {
    // The /discount/<CODE> visit cannot be "un-done": Shopify keeps the code in the
    // cart session (and the discount_code cookie) even after the widget clears its
    // own state, so the code silently re-applied at checkout (bug report 2026-09-03).
    // Since 2024 /cart/update.js removes it server-side (verified: discount_codes=[]
    // stays empty across follow-up mutations); deleting the cookie is belt & braces
    // because Shopify never expires it itself.
    var clearing = fetch(routes().cart_url + '/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: '' }),
      credentials: 'same-origin',
      keepalive: true,
    }).catch(function () {});
    document.cookie = 'discount_code=; Path=/; Max-Age=0; SameSite=Lax';
    return clearing; // clearCode chains the follow-up work on the settled session
  }

  function addGift() {
    if (state.busy || !state.entry || !state.code) return;
    if (giftLine(state.cart, state.code)) return;
    state.busy = true;
    var sections = sectionsToRequest().map(function (s) {
      return s.section;
    });
    fetch(routes().cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(state.entry.variantId), quantity: 1, properties: privProps() }],
        sections: sections,
        sections_url: window.location.pathname,
      }),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (cartData) {
        state.cart = cartData;
        renderSections(cartData);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: cartData });
      })
      .catch(function () {})
      .finally(function () {
        state.busy = false;
        syncWidget();
      });
  }

  function privProps() {
    var props = {};
    props[GIFT_PROP] = state.code;
    return props;
  }

  function removeGift(line) {
    if (state.busy || !line) return Promise.resolve();
    state.busy = true;
    state.selfRemoved = true;
    var sections = sectionsToRequest().map(function (s) {
      return s.section;
    });
    // NOTE: requests must carry a line key from a POST-/discount/ snapshot — the
    // /discount/<CODE> visit that activates a session REGENERATES all cart line
    // keys, so a pre-session key dies with HTTP 400 "keine gültigen ID- oder
    // Zeilenparameter" (live traced 2026-10-20; this, not the URL, was the latent
    // bug behind the "gift line never removed" report).
    return fetch(routes().cart_change_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: line.key,
        quantity: 0,
        sections: sections,
        sections_url: window.location.pathname,
      }),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (cartData) {
        state.cart = cartData;
        renderSections(cartData);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: cartData });
      })
      .catch(function () {})
      .finally(function () {
        state.busy = false;
        state.selfRemoved = false;
        syncWidget();
      });
  }

  // ---------------------------------------------------------------- decisions

  function evaluate(prevCart) {
    if (!state.cart) return;
    var line = giftLine(state.cart);

    if (!state.code || !state.entry) {
      // no active gift code — any leftover gift line belongs to an older code
      if (line) removeGift(line);
      return;
    }

    var base = baseTotal(state.cart);
    if (base < state.entry.threshold) {
      state.declined = false;
      if (line) removeGift(line);
      return;
    }

    // threshold met
    if (line && line.properties[GIFT_PROP] !== state.code) {
      removeGift(line); // stale line from a previous code
      return;
    }
    if (!line) {
      // user-removed detection: line was there in the previous snapshot, gone now,
      // threshold still met, and we did not remove it ourselves -> respect the choice
      var wasThere = prevCart && giftLine(prevCart);
      if (wasThere && !state.selfRemoved) state.declined = true;
      if (!state.declined && !state.busy) addGift();
    }
  }

  // Single-code gate (Aseli decision 2026-10-07): codes never stack. Entering a
  // gift code while a regular code is active (or the other way round) is blocked;
  // switching between two gift codes stays allowed — the old gift line is removed
  // cleanly and the campaigns simply exchange each other. Returns the blocking
  // code ({ code, enteringGift }) or null when the entry may proceed.
  function gateBlocker(newCode, newEntry) {
    // 1) the widget's own active code (chip)
    if (state.code && state.code !== newCode) {
      var oldIsGift = !!state.entry;
      var newIsGift = !!newEntry;
      if (newIsGift !== oldIsGift) return { code: state.code, enteringGift: newIsGift };
      // same kind: gift → gift switch (allowed) or regular → regular (unchanged)
    }
    // 2) codes living only in the server session — applied at checkout or via a
    //    /discount/ marketing link; the chip only shows them after adoption
    var list = (state.cart && state.cart.discount_codes) || [];
    for (var i = 0; i < list.length; i++) {
      var dc = list[i] && list[i].code ? String(list[i].code).toLowerCase() : '';
      if (!dc || dc === newCode || dc === state.code) continue;
      var dcIsGift = !!findEntry(dc);
      if (!!newEntry !== dcIsGift) return { code: dc, enteringGift: !!newEntry };
    }
    return null;
  }

  function applyCode(raw) {
    var code = String(raw || '').trim().toLowerCase();
    if (!code) return;

    // Gate on a FRESH cart snapshot — state.cart may be stale, and codes applied
    // at checkout exist ONLY server-side. A rejected entry must not touch state,
    // session or gift line ("darf nicht hinzugefügt werden").
    withFreshCart(function () {
      var entry = findEntry(code);
      var blocker = gateBlocker(code, entry);
      if (blocker) {
        state.blocked = blocker;
        syncWidget();
        return;
      }

      state.blocked = null;
      state.yielded = false; // fresh entry — a stale yield notice would explain the wrong code
      state.choiceYielded = null; // same for the exclusive-code notice
      state.sessionSetAt = Date.now();
      var previousEntry = state.entry;
      state.code = code;
      state.entry = entry;
      state.declined = false;
      state.draft = code;
      persist();
      syncWidget(); // 1) instant feedback — status + chip show before any network call

      // 2) checkout session FIRST (settles server-side), then decide on the fresh
      //    cart — state.cart may be missing (early entry) or stale; a wrong "not
      //    over threshold" verdict here was the bug that made code entry look dead
      //    until the next cart event (2026-08-24).
      setSessionDiscount(code).then(function () {
        withFreshCart(function () {
          var line = giftLine(state.cart);
          // switching codes (or moving to a regular code) — the old gift line must go:
          // the checkout only takes one code, so the line would be charged at full price
          var codeChanged = previousEntry && (!entry || entry.code !== previousEntry.code);
          if (line && codeChanged) {
            removeGift(line).then(function () {
              maybeAddGift();
              syncWidget();
            });
            return;
          }
          // gift-exclusive code (eimer10): never combines with the threshold gift
          // (the code wins the order's only product-discount slot — live verified
          // 2026-10-20). Announce the retire for EVERY exclusive entry — the
          // choice widget refreshes its snapshot (the /discount/ visit regenerated
          // all line keys!), removes a sitting choice line with valid keys and
          // hides the tile; we own the explanation when a line actually went.
          if (isExclusive(code)) {
            if (choiceLine(state.cart)) state.choiceYielded = code;
            publish(RETIRE_EVENT, { source: SOURCE });
          } else if (!maybeAddGift()) {
            // no cart mutation follows (no gift line to add) — pull fresh cart
            // sections so the server-rendered strip reflects the code session
            refreshSections();
          }
          syncWidget();
        });
      });
    });
  }

  function maybeAddGift() {
    if (state.entry && state.cart && baseTotal(state.cart) >= state.entry.threshold) {
      addGift();
      return true;
    }
    return false;
  }

  function withFreshCart(fn) {
    fetch(routes().cart_url + '.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        state.cart = cart;
        fn();
      })
      .catch(function () {
        /* keep the last snapshot; decisions re-run on the next cart event */
      });
  }

  function clearCode() {
    state.code = null;
    state.entry = null;
    state.declined = false;
    state.yielded = false;
    state.choiceYielded = null;
    state.draft = '';
    state.blocked = null;
    state.sessionClearedAt = Date.now();
    persist();
    syncWidget();
    // Drop the server-side checkout session FIRST (settled), then pull fresh
    // sections and announce the new cart — the strip must return and the choice
    // widget must un-hide its touchpoints now that the exclusive code is gone
    // (browser report 2026-10-20: tile stayed hidden after chip removal).
    clearSessionDiscount().then(function () {
      refreshSections();
      withFreshCart(function () {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: state.cart });
        var line = giftLine(state.cart);
        if (line) removeGift(line);
        syncWidget();
      });
    });
  }

  // ---------------------------------------------------------------- server mirror

  function serverHasCode(cart) {
    // Cart responses expose the live session codes — invalid and not-yet-applicable
    // ones included (flagged applicable:false, verified 2026-09-03), so a code that
    // is missing entirely was removed server-side. Unknown payload shape → keep state.
    // Case-insensitive: checkout-entered codes come back as typed there ("GRUSEL26"),
    // while the widget always lowercases.
    if (!state.code || !cart || !Array.isArray(cart.discount_codes)) return true;
    for (var i = 0; i < cart.discount_codes.length; i++) {
      if (cart.discount_codes[i] && String(cart.discount_codes[i].code || '').toLowerCase() === state.code) {
        return true;
      }
    }
    return false;
  }

  function adoptServerCode(cart) {
    // Codes that live only in the server session (applied at checkout or via a
    // /discount/ marketing link) have no chip — adopt the first one so the
    // single-code gate always stays resolvable: the customer can remove the
    // blocker with the chip's × button instead of a dead-end error message.
    var list = (cart && cart.discount_codes) || [];
    for (var i = 0; i < list.length; i++) {
      var dc = list[i] && list[i].code ? String(list[i].code).toLowerCase() : '';
      if (!dc) continue;
      state.code = dc;
      state.entry = findEntry(dc);
      state.draft = '';
      state.blocked = null;
      persist();
      return;
    }
  }

  function syncFromServer(cart) {
    // Checkout is Shopify territory: removing a code there never notifies the theme,
    // so the chip used to resurrect a code the checkout had dropped (follow-up bug
    // 2026-09-03). Mirror the server session back into the widget on every cart
    // contact. Grace period: right after applyCode the /discount/ fetch may not have
    // settled — the code would be missing from discount_codes for a moment.
    if (!state.code) {
      // grace after clearCode: the update.js discount:'' call may not have settled
      // yet — adopting now would resurrect the code the customer just removed
      if (Date.now() - (state.sessionClearedAt || 0) < 10000) return;
      adoptServerCode(cart);
      return;
    }
    if (Date.now() - (state.sessionSetAt || 0) < 10000) return;
    if (!serverHasCode(cart)) clearCode();
  }

  // ---------------------------------------------------------------- events / init

  // The choice widget removes its _gift_choice line once our gift line lands (the
  // order's only free unit goes to the code gift) and announces it here — the
  // status line then explains the exchange for as long as the code stays active.
  // Payload { source } = code gift took over; { source, code } = a gift-exclusive
  // regular code took over (checkout-entered session, applyCode never ran).
  function onGiftChoiceYielded(data) {
    var code = data && String(data.code || '').toLowerCase();
    if (state.entry) {
      state.yielded = true;
    } else if (code && state.exclusive[code]) {
      state.choiceYielded = code;
    } else {
      return; // nothing active we could explain
    }
    syncWidget();
  }

  document.addEventListener('submit', function (event) {
    var form = event.target.closest(SELECTORS.form);
    if (!form) return;
    event.preventDefault();
    var input = form.querySelector(SELECTORS.input);
    if (input) applyCode(input.value);
  });

  document.addEventListener('input', function (event) {
    var input = event.target.closest(SELECTORS.input);
    if (input) state.draft = input.value;
  });

  document.addEventListener('click', function (event) {
    if (!event.target.closest(SELECTORS.chipClear)) return;
    clearCode();
  });

  function onCartUpdate(data) {
    var prev = state.cart;
    state.cart = (data && data.cartData) || state.cart;
    if (!data || data.source === SOURCE) {
      syncWidget();
      return;
    }
    // gift-exclusive code session with a leftover choice line (code entered at
    // the checkout or via a link): retire it — self-healing on every cart event,
    // idempotent once the line is gone
    var exclusive = activeExclusiveIn(state.cart);
    if (exclusive && choiceLine(state.cart)) {
      state.choiceYielded = exclusive;
      publish(RETIRE_EVENT, { source: SOURCE });
    }
    syncFromServer(state.cart);
    evaluate(prev);
    syncWidget();
  }

  function initCart() {
    return fetch(routes().cart_url + '.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        state.cart = cart;
        if (state.code) state.entry = findEntry(state.code);
        syncFromServer(cart);
        evaluate(null);
        syncWidget();
      })
      .catch(function () {});
  }

  function init() {
    if (typeof subscribe !== 'function' || typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
      return; // Dawn cart scripts missing — widget stays inert
    }
    subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
    subscribe(YIELDED_EVENT, onGiftChoiceYielded);
    state.exclusive = readExclusiveCodes();

    // Dawn swaps cart regions via innerHTML on every change — hydrate NEW widget
    // nodes only. The observer must never react to our own writes, so all writes
    // go through value-guards (setText / hidden-on-change / attribute marking):
    // a re-invocation finds no unhydrated roots and terminates. rAF-batched.
    var hydratePending = false;
    var observer = new MutationObserver(function () {
      if (hydratePending) return;
      hydratePending = true;
      requestAnimationFrame(function () {
        hydratePending = false;
        hydrateRoots();
      });
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });

    var savedCode = null;
    try {
      savedCode = (JSON.parse(localStorage.getItem(LS_KEY) || 'null') || {}).code;
    } catch (err) {
      savedCode = null;
    }
    if (savedCode) {
      state.code = savedCode;
      state.entry = findEntry(savedCode);
    }
    initCart();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
